/**
 * The business registration certificate (KYB) step: a photo or a PDF the
 * applicant already holds, so a file picker rather than the framed camera of
 * ./CaptureDocument — the certificate is an A4 page, often already a PDF. On a
 * phone the picker offers the camera as well.
 *
 * Owns its own POSTs and hands back the next snapshot, the contract the other
 * capture widgets use (./businessDocument has the upload itself).
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ACCEPTED_TYPES,
  problemWith,
  submitBusinessDocument,
  type BusinessDocumentProblem,
} from './businessDocument';
import { postCollectionFlowEvent } from './client';
import type { CollectionFlowSnapshot } from './types';

interface BusinessDocumentStepProps {
  token: string;
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
  /**
   * What a valid file does in place of the upload — the console's flow
   * preview, where nothing leaves the frame. Absent on a case.
   */
  submit?: (file: File) => Promise<void>;
}

export function BusinessDocumentStep({ token, onAdvance, submit }: BusinessDocumentStepProps) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<BusinessDocumentProblem | 'failed' | null>(null);

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again after an error fires `change`.
    event.target.value = '';
    if (!file) return;
    const found = problemWith(file);
    setProblem(found);
    if (found) return;
    setBusy(true);
    try {
      if (submit) await submit(file);
      else onAdvance(await submitBusinessDocument(token, file, postCollectionFlowEvent));
    } catch {
      setProblem('failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1 className="step-title">{t('flow.businessDocument.title')}</h1>
      <p className="step-text">{t('flow.businessDocument.hint')}</p>
      {problem ? (
        <p className="alert" role="alert" data-testid="business-document-error">
          {t(`flow.businessDocument.${problem}`)}
        </p>
      ) : null}
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_TYPES}
        hidden
        data-testid="business-document-input"
        onChange={(event) => void onPick(event)}
      />
      <div className="actions">
        <button type="button" className="btn" disabled={busy} onClick={() => input.current?.click()} data-track="choose_file">
          {busy ? t('flow.businessDocument.uploading') : t('flow.businessDocument.choose')}
        </button>
      </div>
    </>
  );
}
